import {
  faExternalLinkAlt,
  faEye,
  faSort,
  faSortDown,
  faSortUp
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable
} from 'react-table';
import GlobalTableFilter from '../GlobalTableFilter';
import TablePagination from '../TablePagination';

const PropertiesTable = (props) => {
  const [showDeletedProperties, setShowDeletedProperties] = useState(false);

  const getPermissions = (property) => {
    // Find the permissions of the authenticated user
    let canRead = false;
    let canDelete = false;

    if (property.user_permissions) {
      property.user_permissions.forEach((propertyPermission) => {
        if (propertyPermission.code === 'R') canRead = true;
        if (propertyPermission.code === 'D') canDelete = true;
      });
    }

    return {
      canRead: canRead,
      canDelete: canDelete
    };
  };

  const data = useMemo(
    () =>
      props.data.filter((property) => {
        // const permissions = getPermissions(property);
        // return permissions.canRead && showDeletedProperties
        return showDeletedProperties ? true : !property.deleted_at;
      }),
    [props.data, showDeletedProperties]
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Identifier',
        accessor: 'identifier',
        Cell: ({ row, cell }) =>
          row.original.deleted_at ? <del>{cell.value}</del> : cell.value
      },
      {
        Header: 'Name',
        accessor: 'name',
        Cell: ({ row, cell }) =>
          row.original.deleted_at ? <del>{cell.value}</del> : cell.value
      },
      {
        Header: 'Region',
        accessor: 'region.name'
      },
      {
        Header: 'Key Contact',
        filterable: false,
        Cell: ({ row }) => (
          <Button
            variant='link'
            onClick={() => {
              props.linkTo(
                `/properties/property-key-contacts/${row.original.property_key_contact.id}`
              );
            }}
          >
            {row.original.property_key_contact?.user
              ? row.original.property_key_contact?.user?.name
              : row.original.property_key_contact?.name}
          </Button>
        )
      },
      {
        id: 'actions',
        sortable: false,
        filterable: false,
        Header: (tableInstance) => (
          <div className='text-right'>
            <Button
              type='button'
              variant='link'
              className={
                tableInstance.state.showDeletedProperties
                  ? 'text-primary'
                  : 'text-dark'
              }
              onClick={() => {
                setShowDeletedProperties(
                  !tableInstance.state.showDeletedProperties
                );
              }}
            >
              <FontAwesomeIcon icon={faEye} />
            </Button>
          </div>
        ),
        Cell: ({ row }) => {
          // const permissions = getPermissions(row.original);
          return (
            <div className='text-right'>
              {/* Select property */}
              <Button
                type='button'
                variant='link'
                className='text-primary'
                onClick={() => props.changeSelectedProperty(row.original.id)}
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </Button>

              {/* Delete property */}
              {/* {row.original.deleted_at === null && permissions.canDelete ? (
                <Button
                  type='button'
                  variant='link'
                  className='text-danger'
                  onClick={() => props.deleteProperty(row.original.id)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              ) : null} */}
            </div>
          );
        }
      }
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
    state,
    preGlobalFilteredRows,
    setGlobalFilter
  } = useTable(
    {
      columns,
      data,
      useControlledState: (state) => {
        return React.useMemo(
          () => ({
            ...state,
            showDeletedProperties: showDeletedProperties
          }),
          [state, showDeletedProperties]
        );
      }
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  useEffect(() => {
    props.setCurrentPage(pageIndex);
  }, [pageIndex]);

  useEffect(() => {
    gotoPage(props.currentPage);
  }, []);

  return (
    <Fragment>
      <GlobalTableFilter
        preGlobalFilteredRows={preGlobalFilteredRows}
        globalFilter={state.globalFilter}
        setGlobalFilter={setGlobalFilter}
        style={{
          marginBottom: '0.5rem',
          maxWidth: 500
        }}
      />
      <Table responsive {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => {
                return (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    {column.canSort ? (
                      <span className='px-2'>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <FontAwesomeIcon icon={faSortDown} />
                          ) : (
                            <FontAwesomeIcon icon={faSortUp} />
                          )
                        ) : (
                          <FontAwesomeIcon icon={faSort} />
                        )}
                      </span>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
      <TablePagination
        pageCount={pageCount}
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        pageSize={pageSize}
        gotoPage={gotoPage}
        canPreviousPage={canPreviousPage}
        previousPage={previousPage}
        canNextPage={canNextPage}
        nextPage={nextPage}
        setPageSize={setPageSize}
      />
    </Fragment>
  );
};

PropertiesTable.propTypes = {
  data: PropTypes.array.isRequired,
  linkTo: PropTypes.func.isRequired,
  changeSelectedProperty: PropTypes.func.isRequired,
  deleteProperty: PropTypes.func.isRequired
};

export default observer(PropertiesTable);
